import { Button } from '@design-system';
import { missingDetailsCount, totalMissingDetails } from '@shared/consumerPlan';
import { openIntercomChat } from '@/util/intercomUtil';
import { ApplicationConsents } from '../components/application-consents';
import { APPLICATION_SECTION_TITLES } from '../components/application-section-frame';
import { SectionStatusBadge } from '../components/section-status-badge';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { useApplication } from '../hooks/use-application';
import { useApplicationConsents } from '../hooks/use-application-consents';
import { useAddSentApplication } from '../hooks/use-consumer-requests';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { PLAN_READY_STATE } from '../hooks/use-plan-ready-arrival';
import { useRecordFlowCompletion } from '../hooks/use-record-flow-completion';
import { APPLICATION_SECTION_STEPS } from '../lib/steps';
import type { ConsumerStepProps } from './step-props';

export function ApplicationReviewStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { answers, sections, selectedQuote, canSend, send, isSending, sendFailed } = useApplication(clientId);
  const consents = useApplicationConsents(selectedQuote);
  const { recordFlowCompletion, isRecording } = useRecordFlowCompletion(clientId);
  const addSentApplication = useAddSentApplication(clientId);
  if (!selectedQuote) return null;

  // Sending the application finishes the plan. Completion goes into the plan before the application
  // does (see useAddSentApplication), then Your Plan opens and shows the application.
  const finishAfterSending = async (submission: ConsumerApplicationSubmission) => {
    const recording = recordFlowCompletion().catch(() => ({ firstCompletion: false }));
    addSentApplication(submission);
    const { firstCompletion } = await recording;
    goTo('your-plan', { replace: true, state: firstCompletion ? PLAN_READY_STATE : undefined });
  };

  const missingTotal = totalMissingDetails(answers, sections);

  return (
    <StepFrame
      step="application-review"
      title="Ready to send your application to our team?"
      subtitle="We’ll review it before it goes to the insurance company. Sending this doesn’t guarantee coverage. The insurance company decides after underwriting."
      onBack={() => back(APPLICATION_SECTION_STEPS[sections[sections.length - 1]])}
      help={
        <>
          <p>We check the application for errors, missing answers and inconsistencies, and coordinate underwriting with the insurance company. We’ll contact you if anything needs clarification before formal submission.</p>
          <p>This helps avoid delays; it doesn’t guarantee approval or a start date. The insurance company makes the final decision.</p>
          <div className="flex flex-col items-start">
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('options')}>
              Choose different coverage using my answers
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={openIntercomChat}>
              Ask our team for help
            </Button>
          </div>
        </>
      }
      primary={{
        label: 'Send to our team',
        onClick: () => send(consents.consentIds, finishAfterSending),
        disabled: !consents.allAccepted || !canSend,
        loading: isSending || isRecording,
      }}
      error={sendFailed ? 'We couldn’t send your application. Please try again.' : null}
      next="team review, then the insurance company’s decision."
    >
      <SelectedCoverageSummary quote={selectedQuote} />
      <ul className="divide-y rounded-lg border bg-white" data-testid="consumer-application-sections">
        {sections.map(section => {
          const missing = missingDetailsCount(answers, section);
          return (
            <li key={section} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-2">
                <strong className="text-sm text-gray-950">{APPLICATION_SECTION_TITLES[section]}</strong>
                <SectionStatusBadge missing={missing} />
              </div>
              <Button variant="outline" size="sm" onClick={() => goTo(APPLICATION_SECTION_STEPS[section])}>
                Edit
              </Button>
            </li>
          );
        })}
      </ul>
      <p className="text-muted-foreground text-sm">
        {missingTotal
          ? 'It’s okay to send it with gaps. Our team will reach out about anything missing.'
          : 'Everything is filled in. Our team will still check it and reach out with any questions.'}
      </p>
      <ApplicationConsents items={consents.items} accepted={consents.accepted} onChange={consents.setAccepted} />
    </StepFrame>
  );
}
